import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'

import { type AIFieldConfig, getGenerateFieldOptions } from '@/configs/aiFieldConfigs'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { CMFormSection } from '@/screens/ContentModuleFormScreen/ContentModuleFormScreen'
import { buildFAQListString, extractFAQContext } from '@/utils/faqHelpers'

import { getForm, getIdParams, logAIGenerationError } from './services/aiGenerationService'
import type { AICompatibleForm, AIFieldState, AIGenerationContext, FormType } from './types/aiFieldGeneration'

function setFormFieldValue(form: AICompatibleForm, field: string, value: string): void {
  form.setFieldValue(field, value)
}

export function useAIFieldGeneration(props: CMFormSection) {
  const { api } = useGlobalState()

  const presentationStylesQuery = useQuery({
    queryKey: ['presentationStyles'],
    queryFn: () => api.getPresentationStyles(),
  })

  const [fieldStates, setFieldStates] = useState<Record<string, AIFieldState>>({})
  const activeRequestsRef = useRef<Set<string>>(new Set())

  // Cleanup function to cancel active requests and reset state
  useEffect(() => {
    return () => {
      // Clear any active requests on unmount
      activeRequestsRef.current.clear()
      setFieldStates({})
    }
  }, [])

  const getFieldState = useCallback(
    (fieldName: string): AIFieldState => {
      return fieldStates[fieldName] || { state: 'idle', suggestion: null }
    },
    [fieldStates],
  )

  const updateFieldState = useCallback(
    (fieldName: string, updates: Partial<AIFieldState>) => {
      setFieldStates(prev => ({
        ...prev,
        [fieldName]: { ...getFieldState(fieldName), ...updates },
      }))
    },
    [getFieldState],
  )

  // Unified field generation that handles both metadata and specialized endpoints
  const generateFieldUnified = useCallback(
    async (config: AIFieldConfig & { formType: FormType }, context?: AIGenerationContext) => {
      const { formField, formType } = config

      // Check if request is already in progress
      if (activeRequestsRef.current.has(formField)) {
        return
      }

      // Track this request
      activeRequestsRef.current.add(formField)

      updateFieldState(formField, { state: 'loading' })

      try {
        const idParams = getIdParams(formType, props)

        if (config.type === 'metadata') {
          const form = getForm(props, formType) as AICompatibleForm | undefined
          const currentValues = form?.getValues?.()
          const acceptedDataRaw = currentValues ? (currentValues[formField] as unknown) : undefined
          const acceptedDataValue = typeof acceptedDataRaw === 'string' ? acceptedDataRaw : undefined

          // Get current field state to check for pending suggestions
          const currentFieldState = getFieldState(formField)
          const proposedDataValue = currentFieldState.state === 'pending' ? currentFieldState.suggestion : undefined

          const response = await api.safeRequest(api.generateMetadata, {
            ...idParams,
            number_of_options: 1,
            field: config.apiParamField,
            extra_data: {
              field_name: config.apiParamField,
              accepted_data: acceptedDataValue || undefined,
              proposed_data: proposedDataValue || undefined,
            },
          })

          if (response.ok && response.data.generated?.[0]?.[config.apiResponseField]) {
            const suggestion = response.data.generated[0][config.apiResponseField] as string
            updateFieldState(formField, {
              state: 'pending',
              suggestion,
            })
          } else {
            logAIGenerationError('Metadata API response invalid or empty', response, formField)
            updateFieldState(formField, { state: 'idle', suggestion: null })
          }
        } else if (config.type === 'generate') {
          if (formType !== 'cm' && formType !== 'step') {
            throw new Error('Generate endpoint only supports content modules and steps')
          }

          const form = getForm(props, formType) as AICompatibleForm | undefined
          const currentValues = form?.getValues?.()
          const acceptedDataRaw = currentValues ? (currentValues[formField] as unknown) : undefined
          const acceptedDataValue = typeof acceptedDataRaw === 'string' ? acceptedDataRaw : undefined

          // Get current field state to check for pending suggestions
          const currentFieldState = getFieldState(formField)
          const proposedDataValue = currentFieldState.state === 'pending' ? currentFieldState.suggestion : undefined

          // Construct the appropriate ID parameter based on form type
          const idParams = formType === 'cm' ? { task_id: props.taskId! } : { step_id: props.stepId! }

          // Build extra_data object
          const extraData: Record<string, string | undefined> = {}

          // Handle per-FAQ generation (uses FAQ-specific context instead of standard fields)
          if (formField === 'faqSingle' && context?.faqId && context?.faqList) {
            const targetFAQ = context.faqList.find(faq => faq.id === context.faqId)

            if (targetFAQ) {
              // Extract FAQ-specific context (current question/answer and pending suggestion)
              const faqContext = extractFAQContext(targetFAQ)
              if (faqContext.acceptedData) extraData.accepted_data = faqContext.acceptedData
              if (faqContext.proposedData) extraData.proposed_data = faqContext.proposedData

              // Build FAQ list for context (all other FAQs to avoid duplicates)
              const faqList = buildFAQListString(context.faqList, context.faqId)
              if (faqList) extraData.faq_list = faqList
            }
          } else {
            // Standard field generation: use current form values and pending suggestions
            extraData.accepted_data = acceptedDataValue || undefined
            extraData.proposed_data = proposedDataValue || undefined
          }

          if (context?.user_instructions?.trim()) {
            extraData.user_instructions = context.user_instructions.trim()
          }

          // Merge any additional params from config (e.g., user_instructions)
          if (config.additionalParams) {
            Object.keys(config.additionalParams).forEach(key => {
              // Skip dynamic params that are built above
              if (key !== 'faq_list' && typeof config.additionalParams![key] === 'string') {
                extraData[key] = config.additionalParams![key] as string
              }
            })
          }

          // For outline generation, set is_media_mode when delivery style matches the Media mode template
          if (formField === 'outline' && formType === 'cm') {
            const deliveryStyle = props.moduleForm.getValues().deliveryStyle
            const mediaModeTemplate = presentationStylesQuery.data?.find(t => t.name === 'media-mode')
            if (mediaModeTemplate && deliveryStyle?.trim() === mediaModeTemplate.description?.trim()) {
              extraData.is_media_mode = 'true'
            }
          }

          const response = await api.safeRequest(api.generate, {
            ...idParams,
            system_prompt_template_id: config.systemPromptTemplateId,
            message_template_id: formType === 'cm' ? 'generate_cm_message' : 'generate_step_message',
            extra_data: extraData,
          })

          if (response.ok && response.data) {
            // The response is raw text, not JSON formatted
            const rawSuggestion = response.data as string
            const suggestion = rawSuggestion.trim().replace(/\n\s*\n/g, '\n\n')

            updateFieldState(formField, {
              state: 'pending',
              suggestion,
            })
          } else {
            logAIGenerationError('Generate API response invalid or empty', response, formField)
            updateFieldState(formField, { state: 'idle', suggestion: null })
          }
        } else if (config.type === 'specialized') {
          let response: any

          // Handle different endpoint parameter requirements
          if (
            config.endpoint === 'generateDemographic' ||
            config.endpoint === 'generateObjectives' ||
            config.endpoint === 'generateOutline'
          ) {
            // These endpoints only work with content modules (task_id)
            if (formType !== 'cm') {
              throw new Error(`${config.endpoint} only supports content modules`)
            }

            const form = getForm(props, formType) as AICompatibleForm | undefined
            const currentValues = form?.getValues?.()
            const acceptedDataRaw = currentValues ? (currentValues[formField] as unknown) : undefined
            const acceptedDataValue = typeof acceptedDataRaw === 'string' ? acceptedDataRaw : undefined

            // Get current field state to check for pending suggestions
            const currentFieldState = getFieldState(formField)
            const proposedDataValue = currentFieldState.state === 'pending' ? currentFieldState.suggestion : undefined

            response = await api.safeRequest(api[config.endpoint], {
              task_id: props.taskId!,
              extra_data: {
                field_name: config.formField,
                accepted_data: acceptedDataValue || undefined,
                proposed_data: proposedDataValue || undefined,
              },
            })
          } else if (config.endpoint === 'generateFAQs') {
            // FAQs can work with both content modules and steps, but not resources
            if (formType === 'resource') {
              throw new Error('FAQ generation is not supported for resources')
            }
            const faqParams = formType === 'cm' ? { task_id: props.taskId! } : { step_id: props.stepId! }
            response = await api.safeRequest(api[config.endpoint], faqParams)
          } else if (config.endpoint === 'generateVideoMetadata' || config.endpoint === 'generateImageMetadata') {
            // Video and image metadata generation work with resources (resource_id)
            if (formType !== 'resource') {
              throw new Error(`${config.endpoint} only supports resources`)
            }
            if (!props.resourceId) {
              throw new Error(`${config.endpoint} requires a resource_id`)
            }

            const form = getForm(props, formType) as AICompatibleForm | undefined
            const currentValues = form?.getValues?.()

            // Get current title and description values to send as optional context
            const currentTitle = currentValues?.title as string | undefined
            const currentDescription = currentValues?.description as string | undefined

            // Prepare params based on endpoint type
            const params: any = { resource_id: props.resourceId }

            if (config.endpoint === 'generateVideoMetadata') {
              if (currentTitle) params.video_title = currentTitle
              if (currentDescription) params.video_description = currentDescription
            } else if (config.endpoint === 'generateImageMetadata') {
              if (currentTitle) params.image_title = currentTitle
              if (currentDescription) params.image_description = currentDescription
            }

            response = await api.safeRequest(api[config.endpoint], params)
          }

          if (response.ok && response.data?.[config.responseField]) {
            let suggestion: string

            // Handle FAQs which return an array that needs to be serialized
            if (config.responseField === 'questions') {
              suggestion = JSON.stringify(response.data[config.responseField])
            } else {
              suggestion = response.data[config.responseField] as string
            }

            updateFieldState(formField, {
              state: 'pending',
              suggestion,
            })
          } else {
            logAIGenerationError(`${config.endpoint} API response invalid or empty`, response, formField)
            updateFieldState(formField, { state: 'idle', suggestion: null })
          }
        }
      } catch (error) {
        logAIGenerationError('Field generation request failed', error, formField)
        updateFieldState(formField, { state: 'idle', suggestion: null })
      } finally {
        activeRequestsRef.current.delete(formField)
      }
    },
    [api, props, updateFieldState, presentationStylesQuery.data],
  )

  const acceptSuggestion = useCallback(
    (formField: string, type: FormType, updateServerFn?: (field: string) => void) => {
      const fieldState = getFieldState(formField)
      if (fieldState.state !== 'pending' || !fieldState.suggestion) return

      try {
        const form = getForm(props, type)
        if (!form) {
          logAIGenerationError('Form not available', null, formField)
          return
        }

        const currentValues = form.getValues()
        if (!(formField in currentValues)) {
          logAIGenerationError('Form field not found', null, formField)
          return
        }

        // Type-safe form field setter
        setFormFieldValue(form as AICompatibleForm, formField, fieldState.suggestion)
        updateServerFn?.(formField)
        updateFieldState(formField, { state: 'idle', suggestion: null })
      } catch (error) {
        logAIGenerationError('Failed to accept suggestion', error, formField)
        updateFieldState(formField, { state: 'idle', suggestion: null })
      }
    },
    [getFieldState, updateFieldState, props],
  )

  const rejectSuggestion = useCallback(
    (formField: string) => {
      updateFieldState(formField, { state: 'idle', suggestion: null })
    },
    [updateFieldState],
  )

  const generateFieldByName = useCallback(
    (type: FormType, fieldName: string, context?: AIGenerationContext) => {
      const config = getGenerateFieldOptions(type, fieldName)
      if (config) {
        generateFieldUnified(config, context)
      }
    },
    [generateFieldUnified],
  )

  const regenerateFieldByName = useCallback(
    (type: FormType, fieldName: string, context?: AIGenerationContext) => {
      const config = getGenerateFieldOptions(type, fieldName)
      if (config) {
        generateFieldUnified(config, context)
      }
    },
    [generateFieldUnified],
  )

  // Generate field with a custom config (useful for dynamic configs)
  const generateFieldWithConfig = useCallback(
    (type: FormType, config: AIFieldConfig, context?: AIGenerationContext) => {
      generateFieldUnified({ ...config, formType: type }, context)
    },
    [generateFieldUnified],
  )

  // Cleanup function for form resets
  const resetAllFields = useCallback(() => {
    setFieldStates({})
  }, [])

  // Convenience accept functions
  const acceptCMSuggestion = useCallback(
    (formField: string, updateServerFn?: (field: string) => void) => {
      acceptSuggestion(formField, 'cm', updateServerFn)
    },
    [acceptSuggestion],
  )

  const acceptStepSuggestion = useCallback(
    (formField: string, updateServerFn?: (field: string) => void) => {
      acceptSuggestion(formField, 'step', updateServerFn)
    },
    [acceptSuggestion],
  )

  const acceptResourceSuggestion = useCallback(
    (formField: string, updateServerFn?: (field: string) => void) => {
      acceptSuggestion(formField, 'resource', updateServerFn)
    },
    [acceptSuggestion],
  )

  return {
    // State management
    getFieldState,
    resetAllFields,

    // Core generation functions
    acceptSuggestion,
    rejectSuggestion,

    // Configuration-based generation
    generateFieldByName,
    regenerateFieldByName,
    generateFieldWithConfig,

    // Convenience functions for common patterns
    acceptCMSuggestion,
    acceptStepSuggestion,
    acceptResourceSuggestion,
  }
}
